/**
 * Autotest-shaped variant dispatch for the free-user PDF-pack upsell modal.
 * Mirrors `AutotestMailer`. PR 2 ships scaffolding (`ARMS = {}`); the
 * `modal_refactor` AB test verifies parity with the unrefactored sibling.
 * PR 3 amends `ARMS` with the `bulk_download_2026_q2_modal_mechanics` test.
 */

import { ComponentType, ReactNode, createElement } from 'react';

import { Aedu } from 'global';
import {
  abTest as MechanicsV2AbTest,
  TEST_CONFIG as MechanicsV2TestConfig,
} from 'js/ab_tests/tests/bulk_download_2026_q2_modal_mechanics_v2';
import { BulkDownloadTrigger } from 'js/bulk_download/bulk_download_helper';
import { BlurredHiddenThumbs } from 'js/bulk_download/work_cards_shared/BlurredHiddenThumbs';
import { BundleGrid } from 'js/bulk_download/work_cards_shared/BundleGrid';
import {
  FadeFooter,
  PaperPreviewList,
} from 'js/bulk_download/work_cards_shared/PaperPreviewList';
import { RotatingPreview } from 'js/bulk_download/work_cards_shared/RotatingPreview';
import { SourceAnchoredWorkCard } from 'js/bulk_download/work_cards_shared/SourceAnchoredWorkCard';
import {
  BundleWork,
  DateRangeFilter,
} from 'js/bulk_download/work_cards_shared/types';
import { WorkCard } from 'js/bulk_download/work_cards_shared/WorkCard';
import {
  DefaultModalFooter,
  FooterImplProps,
} from 'js/bulk_download/WorkCardsBulkDownloadUpsellModal/DefaultModalFooter';
import { GuaranteeBadge } from 'js/bulk_download/WorkCardsBulkDownloadUpsellModal/GuaranteeBadge';
import { BulletItem } from 'js/bulk_download/WorkCardsBulkDownloadUpsellModal/ModalHeader';
import type { Props as StatelessProps } from 'js/bulk_download/WorkCardsBulkDownloadUpsellModal/StatelessWorkCardsUpsellModal';
import { TrustSignalLine } from 'js/bulk_download/WorkCardsBulkDownloadUpsellModal/TrustSignalLine';
import { MinimalStickyFooter } from 'js/bulk_download/autotest/m-variants/MinimalStickyFooter';
import { PaperBrowserPreviewList } from 'js/bulk_download/autotest/m-variants/PaperBrowserPreviewList';
import { ProgressivePreviewList } from 'js/bulk_download/autotest/m-variants/ProgressivePreviewList';
import { SocialProofFadeFooter } from 'js/bulk_download/autotest/m-variants/SocialProofFadeFooter';
import { SplitVerdictPreviewList } from 'js/bulk_download/autotest/m-variants/SplitVerdictPreviewList';
import { TabbedPreviewList } from 'js/bulk_download/autotest/m-variants/TabbedPreviewList';

// ======================================================================// ModalContext — what we know about the user/work at modal-mount time
// ======================================================================
export type ModalContext = {
  trigger: BulkDownloadTrigger;
};

// === Arms ===
//
// Arm naming: `<title>_v<number>`. Title-first because the arm flows into
// telemetry (`impressionExtra.autotest_bucket`) and dashboards need to be
// self-documenting; the trailing `_v<n>` cross-references variants.md.
//
// 17 arms in mechanics_v2 (control + 10 V-variants + 6 M-variants).
// R-variants dropped in v2 (all significantly worse than control).
// R-arm strings kept in the union for now — handler code still exists
// but is unreachable (resolveArm never returns them).

export type Arm =
  | 'control'
  // V-variants: single surface overrides on the existing modal
  | 'bundle_grid_v6'
  | 'rotating_preview_v7'
  | 'source_anchor_v8'
  | 'first_bullet_personalization_v9'
  | 'blurred_hidden_thumbs_v10'
  | 'bucket_badges_v14'
  | 'trust_signal_v18'
  | 'guarantee_badge_v19'
  | 'full_bundle_v20'
  | 'combined_leaders_v21'
  // R-variants: kept in type for handler compatibility (dead code)
  | 'fallback_first_r1'
  | 'transactional_first_r2'
  | 'social_proof_r3'
  | 'no_modal_inline_r4'
  | 'premium_preview_r5'
  | 'just_this_paper_r6'
  | 'post_download_r7'
  | 'personalization_gate_r8'
  // M-variants: coordinated multi-surface structural changes
  | 'evidence_first_m1'
  | 'progressive_disclosure_m2'
  | 'split_verdict_m3'
  | 'category_tabs_m4'
  | 'paper_browser_m5'
  | 'social_proof_injection_m6';

/** Resolved arm for a user. `undefined` = holdout / not bucketed. */
export type ResolvedArm = Arm | undefined;

export function resolveArm(_ctx: ModalContext): ResolvedArm {
  const arm = MechanicsV2AbTest();
  return arm === 'holdout' ? undefined : arm;
}

// === Surfaces ===

// Keys match `StatelessWorkCardsUpsellModal` prop names so the upsell factory
// can be table-driven (iterate `keyof Surfaces` to build the bag).
type Surfaces = {
  modalTitle: string;
  /** Ready-to-render bullet list. CONTROL emits the canonical four; variants
   * supply a full replacement list. */
  bullets: ReadonlyArray<BulletItem>;
  primaryCtaLabel: string;
  ctaSubtitleSlot: ReactNode;
  trustSignalSlot: ReactNode;
  guaranteeBadgeSlot: ReactNode;
  FooterComponent: ComponentType<FooterImplProps>;
  paperLimit: number;
  mobileFilterPillSlot: ReactNode;
  /** Render function for the source-paper card (the "included work" slot at
   * the top of the preview list). CONTROL renders the existing WorkCard with
   * the "(included)" header. Variants that decorate just the source-paper
   * card (e.g. V8 source-anchor: chip + accent border) override only this
   * surface and compose with whatever `renderPreviewList` is active. */
  renderIncludedWork: (args: {
    sourceWorkPreview: BundleWork | undefined;
    showAnnotations: boolean;
    badgePlacement: 'side' | 'below' | 'above';
  }) => ReactNode;
  /** Render function for the bundle preview list. ModalPreviewSection builds
   * the per-render args (post-filter `relatedWorks` full + `visibleRelatedWorks`
   * post-paperLimit slice, pre-built `previewFooter` and `emptyState` nodes,
   * and the resolved `includedWorkSlot` from `renderIncludedWork`) and calls
   * this to get the preview node back, then splats it. CONTROL renders the
   * vertical `PaperPreviewList`; variants that change layout (e.g. V6 grid,
   * V7 rotating) override this surface. `relatedWorks` is the full filtered
   * list (Premium gets all; basic stops at `paperLimit`); cycling variants
   * (V7) use it to rotate through more than the visible slice. */
  renderPreviewList: (args: {
    relatedWorks: BundleWork[];
    visibleRelatedWorks: BundleWork[];
    paperLimit: number;
    totalRelatedCount: number;
    sourceWorkPreview: BundleWork | undefined;
    includedWorkSlot: ReactNode;
    showAnnotations: boolean;
    badgePlacement: 'side' | 'below' | 'above';
    previewFooter: ReactNode;
    emptyState: ReactNode;
    isMobile: boolean;
    /** V14: show per-card bucket badges. */
    showBucketBadges: boolean;
  }) => ReactNode;
  /** Render function for the preview-list footer. Stateless calls this
   * after computing filter results, so the variant has access to the
   * post-filter `relatedWorks`, `paperLimit`, `isMobile`, and
   * `hiddenPapersCount`. CONTROL renders the gradient `FadeFooter`. */
  renderFadeFooter: (args: {
    hiddenPapersCount: number;
    relatedWorks: ReadonlyArray<BundleWork>;
    paperLimit: number;
    isMobile: boolean;
  }) => ReactNode;
  /** V14 (`bucket_badges_v14`): show bucket chips on each non-source
   *  preview card. CONTROL: false. */
  showBucketBadges: boolean;
  /** M5 (`paper_browser_m5`): hide the filter sidebar so the
   *  master-detail preview list takes the full modal width. */
  hideFilters: boolean;
};

// Args expose raw values (e.g. `totalRelatedCount`); surfaces format for their
// own context — formatting is presentational, not contractual.
type SurfaceArgs = {
  modalTitle: { totalRelatedCount: number };
  bullets: {
    totalRelatedCount: number;
    sourceWorkTitle: string;
    hideIncludedWorkValueProp: boolean;
  };
  primaryCtaLabel: { hasActiveFilters: boolean; visibleCount: number };
  ctaSubtitleSlot: { annualPriceLabel?: string };
  trustSignalSlot: { userId?: string | number | null };
  guaranteeBadgeSlot: void;
  FooterComponent: void;
  paperLimit: { hasActiveFilters: boolean };
  mobileFilterPillSlot: {
    isMobile: boolean;
    dateRange: DateRangeFilter;
    onDateRangeChange: (v: DateRangeFilter) => void;
  };
  // The render functions take their args at call time inside
  // ModalPreviewSection, so they have no boundary-time SurfaceArgs.
  renderIncludedWork: void;
  renderPreviewList: void;
  renderFadeFooter: void;
  showBucketBadges: void;
  hideFilters: void;
};

/** Standard TS trick: distribute over a union to get the intersection of
 * its members. */
type UnionToIntersection<U> = (
  U extends unknown ? (x: U) => void : never
) extends (x: infer I) => void
  ? I
  : never;

/** Intersection of every `SurfaceArgs[S]`, assignable to all of them. Adding
 * a required field to any `SurfaceArgs` breaks this declaration until the
 * field is supplied. */
type AllArgs = UnionToIntersection<
  // `Extract<…, object>` filters `void` (it would intersect every field away).
  Extract<SurfaceArgs[keyof Surfaces], object>
>;

// Tag arg-driven entries with a Symbol so `resolve` can distinguish them from
// values that ARE functions (e.g. `FooterComponent`, a React component) —
// `typeof === 'function'` would fire on both.
const FACTORY = Symbol('SurfaceFactory');

type Factory<S extends keyof Surfaces> = {
  readonly [FACTORY]: true;
  readonly fn: (ctx: ModalContext, args: SurfaceArgs[S]) => Surfaces[S];
};

/** Wrap a `(ctx, args) => value` so `resolve` calls it. Plain values
 * (incl. React components, `null`) go in unwrapped. */
function factory<S extends keyof Surfaces>(
  fn: (ctx: ModalContext, args: SurfaceArgs[S]) => Surfaces[S],
): Factory<S> {
  return { [FACTORY]: true, fn };
}

function isFactory<S extends keyof Surfaces>(
  v: SurfaceOverride<S>,
): v is Factory<S> {
  return (
    v !== null &&
    typeof v === 'object' &&
    (FACTORY as unknown as PropertyKey) in v
  );
}

type SurfaceOverride<S extends keyof Surfaces> = Surfaces[S] | Factory<S>;

function countRelatedWorks(bundleWorks: ReadonlyArray<BundleWork>): number {
  return bundleWorks.filter((w) => w.bucket !== 'thePaperItself').length;
}

/** Canonical four-bullet list when no variant overrides `bullets`. */
function defaultBullets({
  totalRelatedCount,
  hideIncludedWorkValueProp,
}: {
  totalRelatedCount: number;
  hideIncludedWorkValueProp: boolean;
}): BulletItem[] {
  return [
    ...(hideIncludedWorkValueProp
      ? []
      : [{ text: 'The paper you are reading' }]),
    {
      text:
        totalRelatedCount === 0
          ? 'Related papers'
          : totalRelatedCount >= 37
            ? '37+ related papers'
            : `${totalRelatedCount} related papers`,
    },
    { text: 'All premium features' },
    { text: 'Save hours of searching', hideOnMobile: true },
  ];
}

// === CONTROL ===
//
// `satisfies` (not `:`) keeps each entry narrowly typed so callers like
// Premium can read `CONTROL.modalTitle` directly without runtime narrowing.

export const CONTROL = {
  modalTitle: 'Download your PDF Pack',
  bullets: factory<'bullets'>((_ctx, args) => defaultBullets(args)),
  primaryCtaLabel: 'Download PDF Pack for $1',
  ctaSubtitleSlot: null,
  trustSignalSlot: null,
  guaranteeBadgeSlot: null,
  FooterComponent: DefaultModalFooter,
  paperLimit: 6,
  mobileFilterPillSlot: null,
  renderIncludedWork: ({
    sourceWorkPreview,
    showAnnotations,
    badgePlacement,
  }) =>
    sourceWorkPreview
      ? createElement(WorkCard, {
          work: sourceWorkPreview,
          header: 'The paper you are reading (included)',
          showRelevancy: false,
          showAnnotations,
          badgePlacement,
        })
      : null,
  renderPreviewList: ({
    visibleRelatedWorks,
    includedWorkSlot,
    showAnnotations,
    badgePlacement,
    previewFooter,
    emptyState,
    showBucketBadges: badges,
  }) =>
    createElement(PaperPreviewList, {
      works: visibleRelatedWorks,
      includedWorkSlot,
      showAnnotations,
      badgePlacement,
      showBucketBadges: badges,
      maxHeight: '100%',
      footer: previewFooter,
      emptyState,
      style: { flex: 1, minHeight: 0 },
    }),
  renderFadeFooter: ({ hiddenPapersCount }) =>
    createElement(FadeFooter, { hiddenPapersCount }),
  showBucketBadges: false,
  hideFilters: false,
} satisfies { [S in keyof Surfaces]: SurfaceOverride<S> };

// === ARMS ===
//
// Empty in PR 2; PR 3 populates it. Surfaces not overridden by an arm fall
// through to CONTROL.

type ArmSpec = {
  surfaces: Partial<{ [S in keyof Surfaces]: SurfaceOverride<S> }>;
};

const ARMS: Partial<Record<Arm, ArmSpec>> = {
  // V6 — bundle as 3x2 grid. T6 (show the bundle, not the description):
  // vertical list = "papers to scroll." Grid = "bundle as a whole, gained
  // at once." Predicted CTR lift via "I get six things" framing. Mobile
  // collapses to 2x3 via the BundleGrid stylesheet.
  bundle_grid_v6: {
    surfaces: {
      renderPreviewList: ({
        visibleRelatedWorks,
        sourceWorkPreview,
        showAnnotations,
        previewFooter,
        emptyState,
      }) =>
        createElement(BundleGrid, {
          works: visibleRelatedWorks,
          includedWork: sourceWorkPreview,
          showAnnotations,
          footer: previewFooter,
          emptyState,
          style: { flex: 1, minHeight: 0 },
        }),
    },
  },
  // V7 — auto-rotating bundle preview. T9 / T6 extension: instead of
  // implying "+N more papers" via a count, the variant cycles through
  // them visually on a 5s interval — at any given moment shows
  // `paperLimit` papers, but over the modal's lifetime cycles through
  // more. Tests "show how much, through motion" vs "show how much,
  // through count." Desktop only — mobile users have less screen
  // budget for moving elements; mobile falls back to CONTROL's
  // renderer inside the function so impressionExtra still emits V7
  // for slice analysis on `isMobile === false`.
  rotating_preview_v7: {
    surfaces: {
      renderPreviewList: (args) => {
        if (args.isMobile) {
          return CONTROL.renderPreviewList(args);
        }
        return createElement(RotatingPreview, {
          works: args.relatedWorks,
          paperLimit: args.paperLimit,
          totalRelatedCount: args.totalRelatedCount,
          includedWork: args.sourceWorkPreview,
          showAnnotations: args.showAnnotations,
          emptyState: args.emptyState,
          style: { flex: 1, minHeight: 0 },
        });
      },
    },
  },
  // V8 — source paper anchored visual treatment. T8 (anchor on the
  // paper the user was reading) extension: T8's bet (anchoring is
  // real) was previously tested only via the first-bullet copy. The
  // source-paper *card* itself had no real visual treatment — just a
  // small "(included)" header. Variant adds a "Your paper" chip,
  // primary-color border, and slight elevation so the anchor lands.
  // Eligibility falls back inside the resolver: when sourceWorkPreview
  // is undefined (license-blocked source works, or before the API
  // resolves), returns `null` — matches CONTROL.
  source_anchor_v8: {
    surfaces: {
      renderIncludedWork: ({
        sourceWorkPreview,
        showAnnotations,
        badgePlacement,
      }) =>
        sourceWorkPreview
          ? createElement(SourceAnchoredWorkCard, {
              work: sourceWorkPreview,
              showAnnotations,
              badgePlacement,
            })
          : null,
    },
  },
  // V9 — first-bullet literal personalization. T8 falsification: bullet 1
  // currently says "The paper you are reading" (abstract anchoring).
  // Variant replaces it with the source paper's actual title to test
  // whether literal personalization beats abstract anchoring.
  first_bullet_personalization_v9: {
    surfaces: {
      bullets: factory<'bullets'>((_ctx, args) => {
        // `hideIncludedWorkValueProp` falls back to CONTROL inside the
        // factory (rather than gating bucketing) so impressionExtra still
        // emits the V9 arm and sliced analysis covers the eligible subset.
        // Source paper isn't in the bundle here, so the anchor bullet is
        // absent — no slot to personalize.
        if (args.hideIncludedWorkValueProp) {
          return defaultBullets(args);
        }
        const truncated =
          args.sourceWorkTitle.length > 50
            ? `${args.sourceWorkTitle.slice(0, 49)}…`
            : args.sourceWorkTitle;
        // canonical: [anchor, count, premium, search] (anchor is always
        // present here because hideIncludedWorkValueProp === false above)
        const canonical = defaultBullets(args);
        return [{ text: `Including '${truncated}'` }, ...canonical.slice(1)];
      }),
    },
  },
  // V10 — blurred hidden thumbs. Instead of the gradient FadeFooter
  // with "+N more papers", render 1-2 actual hidden papers as blurred
  // WorkCards with a "+N more in pack" caption. Concretizes the gate
  // (vs the gradient + count treatment). Mobile gets 1 thumb, desktop
  // gets 2. Falls back to gradient when bundle has exactly paperLimit
  // papers (no hidden works to show).
  blurred_hidden_thumbs_v10: {
    surfaces: {
      renderFadeFooter: ({
        hiddenPapersCount,
        relatedWorks,
        paperLimit,
        isMobile,
      }) => {
        const works = relatedWorks.slice(
          paperLimit,
          paperLimit + (isMobile ? 1 : 2),
        );
        return works.length > 0
          ? createElement(BlurredHiddenThumbs, { works, hiddenPapersCount })
          : createElement(FadeFooter, { hiddenPapersCount });
      },
    },
  },
  // V14 — per-paper bucket badges. T10 extension: bundle is personalized
  // to the source paper. Currently the personalization is implicit;
  // variant surfaces relational provenance via a chip on each WorkCard
  // (`Cited by`/`Cites`/`Similar`/`By same author`). Tests whether
  // explicit provenance reinforces perceived personalization.
  bucket_badges_v14: {
    surfaces: {
      showBucketBadges: true,
    },
  },
  // V18 — trust signal block below CTA. T14 falsification: T14's bet is
  // that the modal pitches and /checkout owns trust. Variant adds a
  // single-line testimonial below the CTA. Pick is deterministic by
  // user_id (sticky across renders for a returning user). If T14 is
  // right, this is flat. If not, CTR rises.
  trust_signal_v18: {
    surfaces: {
      trustSignalSlot: factory<'trustSignalSlot'>((_ctx, args) =>
        createElement(TrustSignalLine, { userId: args.userId }),
      ),
    },
  },
  // V19 — money-back guarantee badge. T13 extension with risk-reversal:
  // T13's bet is that $1 is the right friction point. Variant adds an
  // explicit risk-reversal alongside ("Cancel anytime · Full refund
  // within 30 days"). Predicted: modest CTR lift via reduced commitment
  // anxiety.
  guarantee_badge_v19: {
    surfaces: {
      guaranteeBadgeSlot: factory<'guaranteeBadgeSlot'>(() =>
        createElement(GuaranteeBadge),
      ),
    },
  },
  // V20 — full bundle visibility. Removes the 6-paper limit so users
  // see every paper the API returned. Tests whether showing the full
  // bundle converts better than the teaser (6 + fade gradient).
  full_bundle_v20: {
    surfaces: { paperLimit: 999 },
  },
  // V21 — combined CTR leaders. Stacks the three V-variants leading
  // control by CTR (V8 + V7 + V14) into one arm. Tests whether the
  // individual directional lifts compound.
  combined_leaders_v21: {
    surfaces: {
      renderIncludedWork: ({
        sourceWorkPreview,
        showAnnotations,
        badgePlacement,
      }) =>
        sourceWorkPreview
          ? createElement(SourceAnchoredWorkCard, {
              work: sourceWorkPreview,
              showAnnotations,
              badgePlacement,
            })
          : null,
      renderPreviewList: (args) => {
        if (args.isMobile) {
          return CONTROL.renderPreviewList(args);
        }
        return createElement(RotatingPreview, {
          works: args.relatedWorks,
          paperLimit: args.paperLimit,
          totalRelatedCount: args.totalRelatedCount,
          includedWork: args.sourceWorkPreview,
          showAnnotations: args.showAnnotations,
          emptyState: args.emptyState,
          style: { flex: 1, minHeight: 0 },
        });
      },
      showBucketBadges: true,
    },
  },
  // R2 — escalation pitch CTA. Shown on the standard upsell modal
  // after the user dismisses the transactional pricing modal.
  // "Download PDF Pack for $1" → "Download unlimited PDF Packs for $1".
  transactional_first_r2: {
    surfaces: {
      primaryCtaLabel: 'Download unlimited PDF Packs for $1',
    },
  },
  // ── M-variants: middle-ground modal mechanics ──────────────────────
  //
  // M-variants keep the modal shell but make bold structural changes via
  // coordinated multi-surface overrides. Each changes 2-4 surfaces.

  // M1 — evidence-first. Remove pitch copy, let papers fill the modal.
  evidence_first_m1: {
    surfaces: {
      bullets: factory<'bullets'>(() => []),
      paperLimit: 12,
      FooterComponent: MinimalStickyFooter,
    },
  },
  // M2 — progressive disclosure. Modal starts collapsed; expands on
  // user interest. CTA label shifts on expand.
  progressive_disclosure_m2: {
    surfaces: {
      renderPreviewList: (args) =>
        createElement(ProgressivePreviewList, {
          relatedWorks: args.relatedWorks,
          visibleRelatedWorks: args.visibleRelatedWorks,
          sourceWorkPreview: args.sourceWorkPreview,
          includedWorkSlot: args.includedWorkSlot,
          showAnnotations: args.showAnnotations,
          badgePlacement: args.badgePlacement,
          previewFooter: args.previewFooter,
          emptyState: args.emptyState,
          showBucketBadges: args.showBucketBadges,
          totalRelatedCount: args.totalRelatedCount,
        }),
      bullets: factory<'bullets'>((_ctx, args) => {
        const items: BulletItem[] = [];
        if (!args.hideIncludedWorkValueProp) {
          items.push({ text: 'The paper you are reading' });
        }
        items.push({
          text:
            args.totalRelatedCount >= 37
              ? '37+ related papers available'
              : `${args.totalRelatedCount} related papers available`,
        });
        return items;
      }),
    },
  },
  // M3 — split verdict. Source paper on the left, bundle on the right.
  split_verdict_m3: {
    surfaces: {
      renderPreviewList: (args) =>
        createElement(SplitVerdictPreviewList, {
          relatedWorks: args.relatedWorks,
          visibleRelatedWorks: args.visibleRelatedWorks,
          sourceWorkPreview: args.sourceWorkPreview,
          showAnnotations: args.showAnnotations,
          badgePlacement: args.badgePlacement,
          previewFooter: args.previewFooter,
          emptyState: args.emptyState,
          showBucketBadges: args.showBucketBadges,
          totalRelatedCount: args.totalRelatedCount,
        }),
      renderIncludedWork: () => null,
    },
  },
  // M4 — category tabs. Browse papers by relationship type.
  category_tabs_m4: {
    surfaces: {
      renderPreviewList: (args) =>
        createElement(TabbedPreviewList, {
          relatedWorks: args.relatedWorks,
          sourceWorkPreview: args.sourceWorkPreview,
          includedWorkSlot: args.includedWorkSlot,
          showAnnotations: args.showAnnotations,
          badgePlacement: args.badgePlacement,
          emptyState: args.emptyState,
          paperLimit: args.paperLimit,
          totalRelatedCount: args.totalRelatedCount,
        }),
      bullets: factory<'bullets'>((_ctx, args) => {
        const items: BulletItem[] = [];
        if (!args.hideIncludedWorkValueProp) {
          items.push({ text: 'The paper you are reading' });
        }
        items.push({
          text: 'Browse by: Citing · Cited by · Similar · By author',
        });
        items.push({ text: 'All premium features' });
        return items;
      }),
      showBucketBadges: true,
    },
  },
  // M5 — paper browser. Master-detail: clickable list replaces the
  // filter sidebar; detail preview panel fills the right side.
  paper_browser_m5: {
    surfaces: {
      hideFilters: true,
      renderPreviewList: (args) =>
        createElement(PaperBrowserPreviewList, {
          relatedWorks: args.relatedWorks,
          visibleRelatedWorks: args.visibleRelatedWorks,
          sourceWorkPreview: args.sourceWorkPreview,
          includedWorkSlot: args.includedWorkSlot,
          showAnnotations: args.showAnnotations,
          badgePlacement: args.badgePlacement,
          previewFooter: args.previewFooter,
          emptyState: args.emptyState,
          showBucketBadges: args.showBucketBadges,
          totalRelatedCount: args.totalRelatedCount,
        }),
    },
  },
  // M6 — social proof injection. R3's persuasion woven into the
  // standard modal: testimonials, researcher counts, university stats.
  social_proof_injection_m6: {
    surfaces: {
      trustSignalSlot: factory<'trustSignalSlot'>((_ctx, args) =>
        createElement(TrustSignalLine, { userId: args.userId }),
      ),
      bullets: factory<'bullets'>((_ctx, args) => {
        const items: BulletItem[] = [];
        if (!args.hideIncludedWorkValueProp) {
          items.push({ text: 'The paper you are reading' });
        }
        items.push({
          text:
            args.totalRelatedCount >= 37
              ? '37+ related papers'
              : `${args.totalRelatedCount} related papers`,
        });
        items.push({ text: 'Trusted by 500,000+ researchers' });
        return items;
      }),
      renderFadeFooter: ({ hiddenPapersCount }) =>
        createElement(SocialProofFadeFooter, { hiddenPapersCount }),
      ctaSubtitleSlot: factory<'ctaSubtitleSlot'>(() =>
        createElement(
          'span',
          {
            style: {
              fontSize: 'var(--type-body-xs-size, 12px)',
              color: 'var(--text-secondary, #535366)',
            },
          },
          'Join 500,000+ researchers',
        ),
      ),
    },
  },
};

// === Generic resolver ===

export function resolve<S extends keyof Surfaces>(
  surface: S,
  ctx: ModalContext,
  arm: ResolvedArm,
  args: SurfaceArgs[S],
): Surfaces[S] {
  if (arm !== undefined) {
    const spec = ARMS[arm];
    const override = spec?.surfaces[surface] as SurfaceOverride<S> | undefined;
    if (override !== undefined) {
      return isFactory(override) ? override.fn(ctx, args) : override;
    }
  }
  // `satisfies` keeps CONTROL narrow for direct callers, but `CONTROL[surface]`
  // under generic `S` widens to a union that doesn't match `SurfaceOverride<S>`.
  // The double-cast bridges that.
  const ctrl = CONTROL[surface] as unknown as SurfaceOverride<S>;
  return isFactory(ctrl) ? ctrl.fn(ctx, args) : ctrl;
}

// === impressionExtra ===
//
// Matches `mailer.rb#add_autotest_log_data` (`mailer.rb:552-559`):
// - `autotest_bucket` always present (defaults to 'control', signaling
//   the surface is wired)
// - `autotest` test-name only when the test is active per its YAML
//
// Activeness is read from the generated test config (`TEST_CONFIG.active`),
// which the YAML's `export_config: true` flag exposes. Single source of
// truth — when the go-live PR flips `active: true`, regenerating the TS
// automatically flips this. Works correctly for holdout users too: they
// get `autotest:` emitted because we key on `active`, not on `arm`.

export function impressionExtra(
  arm: ResolvedArm,
  modalVariant: string,
): Record<string, unknown> {
  const isActive: boolean = MechanicsV2TestConfig.active as boolean;
  return {
    modalVariant,
    autotest_bucket: arm ?? 'control',
    ...(isActive && {
      autotest: 'bulk_download_2026_q2_modal_mechanics_v2',
    }),
  };
}

// === Stateless prop-bag factories ===
//
// Stateless has no defaults for surface props — these are the single
// source of truth.

export type StatelessSurfaces = Required<Pick<StatelessProps, keyof Surfaces>>;

export function statelessSurfacesForUpsell(
  ctx: ModalContext,
  args: {
    bundleWorks: ReadonlyArray<BundleWork>;
    sourceWorkTitle: string;
    hideIncludedWorkValueProp: boolean;
    hasActiveFilters: boolean;
    dateRange: DateRangeFilter;
    onDateRangeChange: (v: DateRangeFilter) => void;
    isMobile: boolean;
  },
): StatelessSurfaces & { arm: ResolvedArm } {
  const arm = resolveArm(ctx);
  const totalRelatedCount = countRelatedWorks(args.bundleWorks);
  const visibleCount = Math.min(totalRelatedCount, 6);
  // `bundleWorks` is input for `totalRelatedCount`, not a surface arg.
  const { bundleWorks: _bw, ...rest } = args;
  const allArgs: AllArgs = {
    ...rest,
    totalRelatedCount,
    visibleCount,
    userId: Aedu.User?.current?.id ?? null,
  };
  // `void`-args surfaces don't intersect with `AllArgs`; cast bridges them.
  // They ignore args at runtime.
  const r = <S extends keyof Surfaces>(surface: S): Surfaces[S] =>
    resolve(surface, ctx, arm, allArgs as SurfaceArgs[S]);

  // `Object.fromEntries(...) as { [S]: Surfaces[S] }` sidesteps a TS
  // contravariance limit on mutable-index assignment.
  const surfaces = Object.fromEntries(
    (Object.keys(CONTROL) as Array<keyof Surfaces>).map((key) => [key, r(key)]),
  ) as { [S in keyof Surfaces]: Surfaces[S] };

  return { arm, ...surfaces };
}

export function statelessSurfacesForPremium(args: {
  bundleWorks: ReadonlyArray<BundleWork>;
  hideIncludedWorkValueProp: boolean;
}): StatelessSurfaces {
  const totalRelatedCount = countRelatedWorks(args.bundleWorks);
  return {
    modalTitle: CONTROL.modalTitle,
    bullets: defaultBullets({
      totalRelatedCount,
      hideIncludedWorkValueProp: args.hideIncludedWorkValueProp,
    }),
    // Premium override — basic-tier 'Download PDF Pack for $1' is wrong here.
    primaryCtaLabel: 'Download PDF Pack',
    // Ignored when `isPremium=true`; passing the canonical default to satisfy
    // the required-prop contract.
    paperLimit: CONTROL.paperLimit,
    FooterComponent: CONTROL.FooterComponent,
    ctaSubtitleSlot: CONTROL.ctaSubtitleSlot,
    trustSignalSlot: CONTROL.trustSignalSlot,
    guaranteeBadgeSlot: CONTROL.guaranteeBadgeSlot,
    mobileFilterPillSlot: CONTROL.mobileFilterPillSlot,
    renderIncludedWork: CONTROL.renderIncludedWork,
    renderPreviewList: CONTROL.renderPreviewList,
    renderFadeFooter: CONTROL.renderFadeFooter,
    showBucketBadges: CONTROL.showBucketBadges,
    hideFilters: CONTROL.hideFilters,
  };
}
